import React, { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { TableContainer, TablePagination } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import {
	getContent,
	getTree,
	ROOT_DIR,
	setLoadingLocation,
	setSort,
	setPage,
	setLimit,
	setBulkVersioningInProgress,
	setDocCenterTableColumnWidth,
	getStatusCounters,
} from "../../slice/document-center-slice";
import { ApiService, ProjectDirectoryService, ProjectDocumentService } from "../../../../api";
import styles from "./DocumentCenterTable.module.css";
import {
	CircularLoader,
	CustomDialog,
	I18nTranslate,
	IconComponent,
	createNotification,
	icon,
} from "../../../../common/components";
import {
	compareInsensitiveCaseString,
	debounce,
	downloadFile,
	endsWithOneOf,
	isNonEmptyArray,
	isNonEmptyObject,
} from "../../../../common/utils";
import MoveDialog from "../move-dialog/MoveDialog";
import ConfirmDeleteDialog from "../confirm-delete-dialog/ConfirmDeleteDialog";
import { environmentSwitch, translate } from "../../../../common/providers";
import DeleteLastVersionDialog from "../delete-last-version-dialog/DeleteLastVersionDialog";
import getDocumentColumns from "./DocumentTableColumn";
import ChangeTypeDialog from "../change-type-dialog/ChangeTypeDialog";
import { FILE_TYPES, FILE_TYPES_EXTENSIONS } from "../../../../common/constants/file-types";
import { DOCUMENT_STATUS } from "../../constants/constants";
import DocumentDragAndDropInfo from "../../../../common/components/drag-and-drop/document-drag-and-drop-info/DocumentDragAndDropInfo";
import BulkVersioningDialog from "../bulk-versioning-dialog/BulkVersioningDialog";
import DocumentCenterVersionDialog from "../document-center-side-panel/tabs/document-center-version-tab/DocumentCenterVersionDialog";
import { useApi, useTrackEvent } from "../../../../common/hooks";
import projectDocumentService from "../../../../api/services/project/project-document-service";
import { setTemporaryDocument } from "../../../my-review/slice/document/document-slice";
import SmartReviewContext from "../../../my-review/context/smart-review-context";
import DocumentCenterSelectionBanner from "./DocumentCenterSelectionBanner";

const debouncedFunction = debounce((func) => func(), 500);

export default function DocumentCenterTable({
	docStatusInfoBannerDisplayed = true,
	helperBoxRef,
	hasBulkVersionCandidates = false,
	onUpdateCandidateCount,
	onImportFile,
	onNavigateToFolder,
	onOpenSidePanel,
}) {
	const dispatch = useDispatch();
	const cancelTokenRef = useRef(null);
	const tableBodyRef = useRef();
	const trackEvent = useTrackEvent();
	const projectId = useSelector(({ context }) => context.project.id);
	const cautionBannerDisabled = useSelector(({ context }) => context.cautionBannerHidden);
	const isLoadingContent = useSelector(({ documentCenter }) => documentCenter.isLoadingContent);
	const isLoadingLocation = useSelector(({ documentCenter }) => documentCenter.isLoadingLocation);
	const currentDirectoryId = useSelector(({ documentCenter }) => documentCenter.currentDirectoryId);
	const hasActionPermission = useSelector(({ documentCenter }) => documentCenter.hasActionPermission);
	const tableColumnOrder = useSelector(({ documentCenter }) => documentCenter.tableColumnOrder);
	const tableColumnWidth = useSelector(({ documentCenter }) => documentCenter.tableColumnWidth);
	const reduxLocation = useSelector(({ documentCenter }) => documentCenter.location);
	const searchDocumentId = useSelector(({ documentCenter }) => documentCenter.searchDocumentId);
	const page = useSelector(({ documentCenter }) => documentCenter.page);
	const limit = useSelector(({ documentCenter }) => documentCenter.limit);
	const sort = useSelector(({ documentCenter }) => documentCenter.sort);
	const direction = useSelector(({ documentCenter }) => documentCenter.direction || undefined);
	const documents = useSelector(({ documentCenter }) => documentCenter.currentDirectoryContent.contents);
	const hasReceivedModifications = useSelector(({ documentCenter }) => documentCenter.hasReceivedModifications);
	const { totalElements } = useSelector(({ documentCenter }) => documentCenter.currentDirectoryContent);
	const docCenterFilters = useSelector(({ documentCenter }) => documentCenter.docCenterFilters);
	const analyzingDocuments = useSelector(({ documentCenter }) => documentCenter.analyzingDocuments);
	const [selectedDocuments, setSelectedDocuments] = useState([]);
	const [countUnsupported, setCountUnsupported] = useState(0);
	const [countInProgress, setCountInProgress] = useState(0);
	const [dragOver, setDragOver] = useState(false);
	const [selectedDirectories, setSelectedDirectories] = useState([]);
	const [selectedRowDir, setSelectedRowDir] = useState([]);
	const [selectedVersionedDocumentName, setSelectedVersionedDocumentName] = useState("");
	const [deleteContent, setDeleteContent] = useState(true);
	const [downloading, setDownloading] = useState(false);
	const [openMoveDialog, dispatchOpenMoveDialog] = useReducer((prev) => !prev, false);
	const [openConfirmDeleteDialog, dispatchOpenConfirmDeleteDialog] = useReducer((prev) => !prev, false);
	const [openDeleteLastVersionDialog, dispatchOpenDeleteLastVersionDialog] = useReducer((prev) => !prev, false);
	const [openChangeTypenDialog, dispatchOpenChangeTypenDialog] = useReducer((prev) => !prev, false);
	const [openVersionDialog, dispatchOpenVersionDialog] = useReducer((prev) => !prev, false);
	const [isValidForVersioning, setIsValidForVersioning] = useState(null);
	const [selectedDoc, setSelectedDoc] = useState(null);
	const [oldDocForVersioning, setOldDocForVersioning] = useState({});
	const [deletingAllVersions, setDeletingAllVersions] = useState(false);
	const [rowPayload, setRowPayload] = useState({});
	const { call: checkDocumentForVersioning } = useApi(projectDocumentService.checkDocumentForVersioning);
	const { call: associateNewVersionOfDoc } = useApi(projectDocumentService.associateNewVersionOfDoc);
	const handleInitBulkVersioning = useCallback(() => {
		dispatch(setBulkVersioningInProgress(true));
		trackEvent("Document_Bulk_V2_Open_Panel");
	}, [dispatch, trackEvent]);

	const handleOpenSP = useCallback(
		(id, type, status) => onOpenSidePanel({ open: true, id, type, status }),
		[onOpenSidePanel]
	);
	const handleOpenMoveDialog = (payload) => {
		if (isNonEmptyObject(payload) && typeof payload === "object") {
			setRowPayload(payload);
			if (compareInsensitiveCaseString(payload.type, FILE_TYPES.FOLDER)) {
				setSelectedRowDir([payload.id]);
			}
		}
		dispatchOpenMoveDialog();
	};
	const handleResetValidation = () => {
		setIsValidForVersioning(null);
	};
	const handleOpenVersionDialog = (document) => {
		dispatchOpenVersionDialog();
		setOldDocForVersioning(document);
		if (!isValidForVersioning) {
			handleResetValidation();
		}
	};
	const generatePayload = (dir, type, id) => {
		const isFolder = type === "folder";
		if (!type || !id) {
			return null;
		}
		return {
			directoryIds: isFolder ? [id] : [],
			documentIds: isFolder ? [] : [id],
			fromDirectoryId: currentDirectoryId,
			...(dir && { toDirectoryId: dir }),
		};
	};
	const handleDownload = (payload) => {
		setDownloading(true);
		const { type, id } = payload || {};
		const downloadPayload =
			type && id
				? generatePayload(null, type, id)
				: {
						fromDirectoryId: currentDirectoryId,
						directoryIds: selectedDirectories,
						documentIds: selectedDocuments,
				  };

		if (!downloadPayload) {
			setDownloading(false);
			return;
		}
		ProjectDirectoryService.download({ projectId }, downloadPayload, cancelTokenRef.current.token)
			.then(({ data, filename }) => {
				downloadFile({ data, filename, filetype: "zip" });
			})
			.catch(console.error)
			.finally(() => setDownloading(false));
	};

	const handleDownloadRowDoc = (payload) => {
		if (isNonEmptyObject(payload) && typeof payload === "object") {
			handleDownload(payload);
		}
	};
	const columns = useMemo(
		() =>
			getDocumentColumns({
				hasActionPermission,
				onOpenDownloadDialog: handleDownloadRowDoc,
				onOpenMoveDialog: handleOpenMoveDialog,
				onOpenVersionDialog: handleOpenVersionDialog,
				onInitBulkVersioning: handleInitBulkVersioning,
				onOpenSP: handleOpenSP,
				columnOrder: tableColumnOrder,
			}).map((column) =>
				tableColumnWidth?.[column.field]
					? { ...column, flex: 0, width: tableColumnWidth[column.field] }
					: column
			),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[hasActionPermission, tableColumnOrder, tableColumnWidth, handleInitBulkVersioning, handleOpenSP]
	);
	const resetSelections = useCallback(() => {
		setSelectedDocuments([]);
		setCountUnsupported(0);
		setCountInProgress(0);
		setSelectedDirectories([]);
	}, []);

	useEffect(() => {
		cancelTokenRef.current = ApiService.getCancelTokenSource();
		return () => {
			ApiService.cancelTokens(cancelTokenRef.current);
		};
	}, []);
	useEffect(() => {
		if (!hasReceivedModifications) {
			return;
		}
		onOpenSidePanel({ open: false, id: null, type: null, status: null });
		if (openMoveDialog) {
			dispatchOpenMoveDialog();
		}
	}, [hasReceivedModifications, openMoveDialog, onOpenSidePanel]);
	useEffect(() => {
		if (!openMoveDialog) {
			setSelectedRowDir([]);
		}
	}, [openMoveDialog]);

	const location = useLocation().search;
	const isRoot = reduxLocation?.[reduxLocation.length - 1]?.id === "root";
	useEffect(() => {
		if (isLoadingLocation) {
			dispatch(setLoadingLocation(false));
		} else {
			const urlDirectory = new URLSearchParams(location).get("directory");
			const currentDirectory = reduxLocation?.[reduxLocation.length - 1];
			if (
				isNonEmptyArray(reduxLocation) &&
				((currentDirectory?.id === "root" && !urlDirectory) ||
					currentDirectory?.id === Number.parseInt(urlDirectory, 10))
			) {
				debouncedFunction(() => {
					dispatch(getStatusCounters({ projectId, token: cancelTokenRef.current.token }));
				});
				dispatch(
					getContent({
						projectId,
						directoryId: urlDirectory || "root",
						page,
						limit,
						sort,
						direction,
						searchDocumentId,
						filters: docCenterFilters.qualityprogress,
						token: cancelTokenRef.current.token,
					})
				);
			}
		}
		return resetSelections;
	}, [
		location,
		dispatch,
		resetSelections,
		isLoadingLocation,
		projectId,
		searchDocumentId,
		sort,
		reduxLocation,
		docCenterFilters,
		direction,
		page,
		limit,
	]);

	useEffect(() => {
		if (!Array.isArray(documents) || documents.length === 0) {
			return;
		}
		let names = "";
		documents
			.filter((document) => selectedDocuments.includes(document.id) && document.version > 1)
			.forEach((document) => {
				names = ` ${names} ${document.label}`;
			});

		setSelectedVersionedDocumentName(names);
	}, [documents, selectedDocuments]);

	useEffect(() => {
		if (!Array.isArray(documents) || documents.length === 0) {
			return;
		}
		let names = "";
		documents
			.filter((document) => selectedDirectories.includes(document.id) && document.previousV >= 1)
			.forEach((document) => {
				names = ` ${names} ${document.label}`;
			});
	}, [documents, selectedDirectories]);
	const handleClickRow = ({ row }) => {
		const { type, id, status } = row;
		if (compareInsensitiveCaseString(type, FILE_TYPES.FOLDER)) {
			dispatch(setPage(0));
			onNavigateToFolder({ id });
		} else if (status !== DOCUMENT_STATUS.IN_PROGRESS) {
			handleOpenSP(id, type, status);
		}
	};
	const resetTreeAndContent = () => {
		debouncedFunction(() => {
			dispatch(getStatusCounters({ projectId, token: cancelTokenRef.current.token }));
		});
		dispatch(getTree({ projectId, token: cancelTokenRef.current.token }));
		dispatch(
			getContent({
				projectId,
				directoryId: currentDirectoryId,
				page,
				limit,
				sort,
				direction,
				filters: docCenterFilters.qualityprogress,
				token: cancelTokenRef.current.token,
			})
		);
		setDeleteContent(true);
		resetSelections();
	};
	const handleDelete = (id) => {
		setDeletingAllVersions(false);
		const srContext = SmartReviewContext.getContext();
		const deletePayload = { directoryIds: selectedDirectories, documentIds: selectedDocuments };
		if (id) {
			deletePayload.directoryIds = [];
			deletePayload.documentIds = [id];
		}
		ProjectDirectoryService.deleteSelection(
			{ projectId, fromDirectoryId: currentDirectoryId },
			{
				fromDirectoryId: currentDirectoryId,
				...deletePayload,
				deleteContent,
			},
			cancelTokenRef.current.token
		)
			.then(() => {
				resetTreeAndContent();
				onUpdateCandidateCount();
				if (srContext && selectedDocuments.includes(srContext?.document)) {
					dispatch(setTemporaryDocument(null));
					SmartReviewContext.clearContext();
				}
			})
			.catch(console.error);
	};
	const handleOpenDeleteDialog = () => {
		const selectedFile = documents.filter(
			(document) =>
				(selectedDocuments.includes(document.id) &&
					compareInsensitiveCaseString(document.type, FILE_TYPES.FILE)) ||
				endsWithOneOf(`.${document.type}`, FILE_TYPES_EXTENSIONS)
		);
		if (selectedDirectories.length > 0) {
			dispatchOpenConfirmDeleteDialog();
		} else if (selectedVersionedDocumentName.length > 0) {
			dispatchOpenDeleteLastVersionDialog();
		} else if (selectedFile.length > 0) {
			dispatchOpenConfirmDeleteDialog();
		} else {
			handleDelete();
		}
	};
	const handleSetDeleteContent = () => {
		setDeleteContent((prev) => !prev);
	};
	const handleConfirmDeleteDialog = () => {
		dispatchOpenConfirmDeleteDialog();
		handleDelete();
	};
	const handleOpenTypeDialog = () => {
		dispatchOpenChangeTypenDialog();
	};
	const handleSubmitChangeType = (submitedType) => {
		ProjectDocumentService.updateDocumentTypes(
			{ projectId },
			{
				docIds: isNonEmptyArray(selectedDocuments) ? selectedDocuments : undefined,
				dirIds: isNonEmptyArray(selectedDirectories) ? selectedDirectories : undefined,
				type: submitedType,
			},
			cancelTokenRef.current.token
		)
			.then(resetTreeAndContent)
			.catch(console.error)
			.finally(() => dispatchOpenChangeTypenDialog());
	};
	const handleMove = (directoryId) => {
		dispatchOpenMoveDialog();
		const toDirectoryId =
			typeof directoryId === "string" && directoryId?.toLowerCase() === ROOT_DIR?.toLowerCase()
				? null
				: directoryId;
		const hasRowPayload = !!(rowPayload.type && rowPayload.id);
		const payload = hasRowPayload
			? generatePayload(toDirectoryId, rowPayload.type, rowPayload.id)
			: {
					toDirectoryId,
					directoryIds: selectedDirectories,
					documentIds: selectedDocuments,
					fromDirectoryId: currentDirectoryId,
			  };
		ProjectDirectoryService.moveDirectoryContent({ projectId }, payload, cancelTokenRef.current.token)
			.then(resetTreeAndContent)
			.catch(console.error);
	};
	const handleCheckDocumentForVersioning = (doc) => {
		checkDocumentForVersioning({ newDocId: doc?.id })
			.then((data) => {
				setIsValidForVersioning(data.value);
				setSelectedDoc(doc);
			})
			.catch((err) => {
				console.error(err);
			});
	};
	const handleValidateVersioning = () => {
		associateNewVersionOfDoc({ newDocId: selectedDoc?.id, oldDocId: oldDocForVersioning?.id })
			.then(() => {
				handleOpenVersionDialog();
				trackEvent("Document_Manual_V2_Correspondance");
			})
			.catch((err) => {
				console.error(err);
			});
	};
	const handleSort = (sortBy) => {
		if (sort === sortBy) {
			dispatch(setSort({ sort: sortBy, direction: direction === "ASC" ? "DESC" : "ASC" }));
		} else {
			dispatch(setSort({ sort: sortBy, direction: "ASC" }));
		}
		resetSelections();
	};
	const handleChangePage = (_, p) => {
		if (totalElements > 0) {
			dispatch(setPage(p));
		}
		resetSelections();
	};
	const handleChangeLimitPerPage = (e) => {
		if (totalElements > 0) {
			dispatch(setLimit(e.target?.value || 25));
			dispatch(setPage(0));
		}
		resetSelections();
	};
	const handleSelect = (id, type) => {
		if (compareInsensitiveCaseString(type, FILE_TYPES.FOLDER)) {
			let tempSelectedDirectories = [...selectedDirectories];
			const index = tempSelectedDirectories.indexOf(id);
			if (index === -1) {
				tempSelectedDirectories = [id, ...tempSelectedDirectories];
			} else {
				tempSelectedDirectories.splice(index, 1);
			}
			setSelectedDirectories(tempSelectedDirectories);
		} else {
			let tempSelectedDocuments = [...selectedDocuments];
			const index = tempSelectedDocuments.indexOf(id);
			if (index === -1) {
				tempSelectedDocuments = [id, ...tempSelectedDocuments];
				if (compareInsensitiveCaseString(type, FILE_TYPES.UNSUPPORTED)) {
					setCountUnsupported((prev) => prev + 1);
				}
				if (documents.find((doc) => doc.id === id).status === DOCUMENT_STATUS.IN_PROGRESS) {
					setCountInProgress((prev) => prev + 1);
				}
			} else {
				tempSelectedDocuments.splice(index, 1);
				if (compareInsensitiveCaseString(type, FILE_TYPES.UNSUPPORTED)) {
					setCountUnsupported((prev) => prev - 1);
				}
				if (documents.find((doc) => doc.id === id).status === DOCUMENT_STATUS.IN_PROGRESS) {
					setCountInProgress((prev) => prev - 1);
				}
			}
			setSelectedDocuments(tempSelectedDocuments);
		}
	};
	const handleSelectAll = () => {
		if (selectedDirectories.length > 0 || selectedDocuments.length > 0) {
			resetSelections();
			setCountUnsupported(0);
			setCountInProgress(0);
		} else {
			let tempSelectedDocuments = [];
			let tempSelectedDirectories = [];
			documents.forEach((document) => {
				if (compareInsensitiveCaseString(document.type, FILE_TYPES.FOLDER)) {
					tempSelectedDirectories = [document.id, ...tempSelectedDirectories];
				} else {
					tempSelectedDocuments = [document.id, ...tempSelectedDocuments];
					if (compareInsensitiveCaseString(document.type, FILE_TYPES.UNSUPPORTED)) {
						setCountUnsupported((prev) => prev + 1);
					}
					if (documents.find((doc) => doc.id === document.id).status === DOCUMENT_STATUS.IN_PROGRESS) {
						setCountInProgress((prev) => prev + 1);
					}
				}
			});
			setSelectedDirectories(tempSelectedDirectories);
			setSelectedDocuments(tempSelectedDocuments);
		}
	};
	const handleDragOver = (event) => {
		event.preventDefault();
		if (!hasActionPermission && !dragOver) {
			return;
		}
		setDragOver(true);
	};
	const handleDragLeave = (event) => {
		event.preventDefault();
		if (!hasActionPermission && dragOver) {
			return;
		}
		if (!event.relatedTarget || !event.currentTarget.contains(event.relatedTarget)) {
			setDragOver(false);
		}
	};
	const handleDropUpload = (event) => {
		event.preventDefault();
		if (!hasActionPermission) {
			return;
		}
		setDragOver(false);
		const allowedFiles = Object.values(event.dataTransfer.files).filter((file) => typeof file !== "number");
		if (Object.values(event.dataTransfer.files).length > allowedFiles.length) {
			createNotification({
				type: "error",
				message: translate("common:upload-file.bad-format"),
			});
		}
		if (allowedFiles.length > 0) {
			onImportFile(allowedFiles);
		}
	};
	if ((isLoadingContent && page === 0) || isLoadingLocation) {
		return (
			<div className={styles.table__loader}>
				<CircularLoader color="primary" size={75} />
			</div>
		);
	}
	const handleCloseLastVersionDialog = () => {
		dispatchOpenDeleteLastVersionDialog();
		setDeletingAllVersions(false);
	};
	const getRowId = (document) => `${document.id}-${document.type}`;
	const rowSelectionModel = isNonEmptyArray(documents)
		? documents
				.filter((document) =>
					compareInsensitiveCaseString(document.type, FILE_TYPES.FOLDER)
						? selectedDirectories.includes(document.id)
						: selectedDocuments.includes(document.id)
				)
				.map(getRowId)
		: [];
	const handleRowSelectionModelChange = (newSelectionModel) => {
		const diff = Math.abs(newSelectionModel.length - rowSelectionModel.length);
		if (diff !== 1) {
			handleSelectAll();
			return;
		}
		const changedRowId =
			newSelectionModel.length > rowSelectionModel.length
				? newSelectionModel.find((rowId) => !rowSelectionModel.includes(rowId))
				: rowSelectionModel.find((rowId) => !newSelectionModel.includes(rowId));
		const changedDocument = documents.find((document) => getRowId(document) === changedRowId);
		if (changedDocument) {
			handleSelect(changedDocument.id, changedDocument.type);
		}
	};
	const sortField = sort;
	const sortModel = sortField ? [{ field: sortField, sort: direction?.toLowerCase() }] : [];
	const handleSortModelChange = (newSortModel) => {
		const sortBy = newSortModel[0]?.field;
		if (sortBy) {
			handleSort(sortBy);
		}
	};
	const handleColumnResize = ({ colDef, width }) => {
		dispatch(setDocCenterTableColumnWidth({ ...tableColumnWidth, [colDef.field]: width }));
	};
	return (
		<>
			<TableContainer
				className={environmentSwitch({ main: styles.tableContainer, veolia: styles["tableContainer--veolia"] })}
				style={{
					height: `calc(100vh - ${
						259 +
						(cautionBannerDisabled ? 0 : 57) +
						(docStatusInfoBannerDisplayed ? 96 : 0) +
						(hasBulkVersionCandidates ? (helperBoxRef.current?.offsetHeight || 0) + 14 : 0) +
						(docStatusInfoBannerDisplayed && hasBulkVersionCandidates ? 96 : 0)
					}px)`,
				}}
			>
				<span
					ref={tableBodyRef}
					className={styles.dragover}
					data-dragover={dragOver}
					data-is-empty-root={!isNonEmptyArray(documents) && isRoot}
					id="tour-document-center-document-list"
					onDragLeave={handleDragLeave}
					onDragOver={handleDragOver}
					onDrop={handleDropUpload}
				>
					{selectedDirectories.length + selectedDocuments.length > 0 && (
						<DocumentCenterSelectionBanner
							hasActionPermission={hasActionPermission}
							hasInProgress={countInProgress > 0}
							hasUnsupported={countUnsupported > 0}
							selectionCount={selectedDirectories.length + selectedDocuments.length}
							onDownload={handleDownload}
							onOpenDeleteDialog={handleOpenDeleteDialog}
							onOpenMoveDialog={handleOpenMoveDialog}
							onOpenTypeDialog={handleOpenTypeDialog}
						/>
					)}
					{(isNonEmptyArray(documents) && (
						<DataGrid
							checkboxSelection
							disableRowSelectionOnClick
							hideFooter
							className={styles.dataGrid}
							columns={columns}
							getRowId={getRowId}
							rowHeight={40}
							rows={documents}
							rowSelectionModel={rowSelectionModel}
							sortingMode="server"
							sortingOrder={["asc", "desc"]}
							sortModel={sortModel}
							onColumnWidthChange={handleColumnResize}
							onDelete={handleDelete}
							onRowClick={handleClickRow}
							onRowSelectionModelChange={handleRowSelectionModelChange}
							onSortModelChange={handleSortModelChange}
						/>
					)) ||
						(isNonEmptyObject(docCenterFilters.qualityprogress) && (
							<span className={styles.table_empty}>
								{translate("document-center.table.empty-state.no-filter-match")}
							</span>
						)) ||
						(isRoot && (
							<div className={styles.table_empty_state}>
								<div className={styles.emptyWelcome}>
									<div>
										<IconComponent
											className={styles.emptyStateIcon}
											color="var(--blue-500)"
											icon={icon.faCloudDownload}
										/>
									</div>
									<div>{translate("document-center.table.empty.welcome")}</div>
								</div>
								<div>{translate("document-center.table.empty.message")}</div>
								<div>
									<I18nTranslate translationKey="document-center.table.empty.additional-information" />
								</div>
							</div>
						)) || <span className={styles.table_empty}>{translate("document-center.table.empty")}</span>}
					{dragOver && <DocumentDragAndDropInfo className={styles.dragDropInfo} />}
				</span>
			</TableContainer>
			{isNonEmptyArray(documents) && (
				<TablePagination
					classes={{ root: styles.table__pagination }}
					component="div"
					count={totalElements || 0}
					page={page}
					rowsPerPage={limit}
					rowsPerPageOptions={[10, 25, 50]}
					onPageChange={handleChangePage}
					onRowsPerPageChange={handleChangeLimitPerPage}
				/>
			)}
			<MoveDialog
				numberOfElements={selectedDirectories.length + selectedDocuments.length}
				open={openMoveDialog}
				selectedFolders={selectedRowDir.length > 0 ? selectedRowDir : selectedDirectories}
				onClose={handleOpenMoveDialog}
				onMove={handleMove}
			/>
			<ChangeTypeDialog
				open={openChangeTypenDialog}
				onClose={handleOpenTypeDialog}
				onSubmit={handleSubmitChangeType}
			/>
			<ConfirmDeleteDialog
				deleteContent={deleteContent}
				isFolder={selectedDirectories.length > 0}
				open={openConfirmDeleteDialog}
				text={translate(
					selectedDocuments.length > 0 && selectedDirectories.length === 0
						? "document-center.table.confirm-delete.text.file"
						: "document-center.table.confirm-delete.text.folder"
				)}
				textSubDir={translate("document-center.table.confirm-delete.sub-text.folder")}
				onClose={dispatchOpenConfirmDeleteDialog}
				onConfirm={handleConfirmDeleteDialog}
				onSetDeleteContent={handleSetDeleteContent}
			/>
			<DeleteLastVersionDialog
				deletingAllVersions={deletingAllVersions}
				open={openDeleteLastVersionDialog}
				onClose={handleCloseLastVersionDialog}
				onConfirm={handleDelete}
			/>
			<CustomDialog
				loader
				maxWidth="xs"
				open={downloading}
				subTitle={translate("document-center.download-process-dialog.file.helper-text")}
				title={translate("document-center.download-process-dialog.file.main-text")}
			/>
			<BulkVersioningDialog />
			{analyzingDocuments === 0 && (
				<DocumentCenterVersionDialog
					hasSearch=""
					isValid={isValidForVersioning}
					open={openVersionDialog}
					selectedDocId={oldDocForVersioning?.id}
					selectedDocumentLabel={oldDocForVersioning?.label}
					onChange={handleCheckDocumentForVersioning}
					onCloseDialog={handleOpenVersionDialog}
					onResetValidation={handleResetValidation}
					onValidateNewDocument={handleValidateVersioning}
				/>
			)}
		</>
	);
}
